import { startCase } from 'lodash-es';
import { toBlob, toPng } from 'html-to-image';
import { TABLE_KEYS_TO_EXCLUDE } from '@/components/insights/helpers';
import { toFileNameFormat } from '@/helpers/date';

/**
 * @description A helper function designed to properly handle each csv cell's value.
 * It specifically handles if a string has a comma in its value or if the value is invalid
 * @param {any} value
 * @returns {any} The cell's value properly formatted
 */
export const getValidValue = (value) => {
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  if (value || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }
  return '';
};

/**
 * @param {object} params object parameter
 * @param {string | object} params.json
 * @param {Array} params.blankColumnNames
 * @description A helper function for converting json or javascript objects to a CSV
 * @returns {string} Comma separate values (CSV)
 */
export const jsonToCSV = ({ json, blankColumnNames = [] }) => {
  const data = typeof json === 'string' ? JSON.parse(json) : json;
  let commaSeparatedString = '';

  if (Array.isArray(data)) {
    const keys = Object.keys(data[0]);
    const formattedKeys = keys.map((key) => (blankColumnNames.includes(key) ? '' : `"${startCase(key)}"`));
    commaSeparatedString = [formattedKeys.join(','), data.map((row) => keys.map((key) => getValidValue(row[key])).join(',')).join('\n')].join('\n');
  } else {
    const keys = Object.keys(data);
    const formattedKeys = keys.map((key) => (blankColumnNames.includes(key) ? '' : startCase(key)));
    commaSeparatedString = [formattedKeys.join(','), keys.map((key) => getValidValue(data[key])).join(',')].join('\n');
  }
  return commaSeparatedString;
};

/**
 * @description Takes an array of arrays and makes a CSV. Each child array is a row of the CSV
 * @param {Array} array
 * @returns {string} Comma separate values (CSV)
 */
export const arrayToCSV = ((array) => {
  let commaSeparatedString = '';

  array.forEach((subArray, i) => {
    if (Array.isArray(subArray)) commaSeparatedString += subArray.map((subArrItem) => getValidValue(subArrItem)).join(',');
    if (array.length - 1 !== i) commaSeparatedString += '\n';
  });

  return commaSeparatedString;
});

export const clickToDownload = (url, fileName) => {
  const downloadEl = document.createElement('a');

  downloadEl.href = url;
  if (fileName) {
    downloadEl.download = fileName;
  }

  document.body.appendChild(downloadEl);
  downloadEl.click();
  document.body.removeChild(downloadEl);
};

export const handleCSVDownload = ({
  commaSeparatedString = '', selector = '', autoClick = false, title = '',
}) => {
  const csvBlob = new Blob([commaSeparatedString]);
  const url = URL.createObjectURL(csvBlob);

  if (autoClick) {
    clickToDownload(url, title);
  } else {
    const downloadEl = document.querySelector(selector);

    downloadEl.href = url;
    downloadEl.download = title;
  }
};

/**
 * @param {object} params object parameter
 * @param {string | object} params.json
 * @param {string} params.selector
 * @param {string} params.title
 * @param {Array} params.blankColumnNames
 * @param {boolean} params.autoClick
 * @description Converts a javascript object or
 * json string to csv and attaches it to an anchor element or triggers a download
 */
const jsonToCSVDownload = ({
  json, selector = '', title = 'export.csv', blankColumnNames = '', autoClick = false,
}) => {
  if ((Array.isArray(json) && json.length === 0) || (typeof json === 'object' && Object.keys(json).length === 0)) return;

  const commaSeparatedString = jsonToCSV({ json, blankColumnNames });
  handleCSVDownload({
    commaSeparatedString,
    selector,
    autoClick,
    title,
  });
};

/**
 * @param {object} params object parameter
 * @param {Array} params.array
 * @param {string} params.selector
 * @param {string} params.title
 * @param {boolean} params.autoClick
 * @description Converts a javascript array to csv
 * and attaches it to an anchor element or triggers a download
 */
export const arrayToCSVDownload = ({
  array, selector = '', title = 'export.csv', autoClick = false,
}) => {
  if (array.length === 0) return;

  const commaSeparatedString = arrayToCSV(array);

  handleCSVDownload({
    commaSeparatedString,
    selector,
    autoClick,
    title,
  });
};

const getDashboardValue = (value) => {
  if (typeof value === 'object') return value.value;
  return value;
};

/* Implementation Specific Functions */

export const assignUsersToCSVDownloadLink = ({
  engagementUsers, dateRange, projectName, sortDesc = true, sortBy = 'avgScore',
}) => {
  const urlBase = `${window.location.origin || 'https://app.datapeople.io'}/app/insights/usage`;

  const sortedUsers = engagementUsers.sort((a, b) => {
    if (a[sortBy] > b[sortBy]) return sortDesc ? -1 : 1;
    if (a[sortBy] < b[sortBy]) return sortDesc ? 1 : -1;
    return 0;
  });

  const csvFormattedUsers = sortedUsers.map((user) => {
    const queryObj = {};

    if (dateRange?.from && dateRange?.to) {
      const { from, to } = dateRange;
      queryObj.from = from;
      queryObj.to = to;
    }

    if (user?.id) {
      queryObj.user = user.id;
    }

    const query = new URLSearchParams(queryObj);

    const url = `${urlBase}?${query}`;

    return {
      name: user.name || '',
      link: url,
      average_score: user.avgScore,
      total_revisions: user.totalRevisions,
      posts_edited: user.postsEdited,
    };
  });

  const dateString = dateRange?.from && dateRange?.to
    ? `-${toFileNameFormat(dateRange.from)}-${toFileNameFormat(dateRange.to)}`
    : '';

  const name = `${projectName}-usage-engagement${dateString}.csv`;
  jsonToCSVDownload({ json: csvFormattedUsers, autoClick: true, title: name });
};

export const hideElementsForDownload = (containerEl) => {
  const ignore = containerEl.querySelectorAll('[data-png-ignore]');

  // eslint-disable-next-line no-param-reassign
  ignore.forEach((el) => { el.style.opacity = 0; });

  const remove = containerEl.querySelectorAll('[data-png-remove]');

  // Store original display values before hiding
  const originalRemoveStyles = [];

  remove.forEach((el, index) => {
    originalRemoveStyles[index] = el.style.display;

    // eslint-disable-next-line no-param-reassign
    el.style.display = 'none';
  });

  const showHiddenDownloadElements = () => {
    // eslint-disable-next-line no-param-reassign
    ignore.forEach((el) => { el.style.opacity = 1; });

    remove.forEach((el, index) => {
      // eslint-disable-next-line no-param-reassign
      el.style.display = originalRemoveStyles[index] || '';
    });
  };

  return { showHiddenDownloadElements };
};

export const getPNGBlob = async ({
  backgroundColor,
  containerSelector,
  calcWidth = (el) => el.clientWidth,
  style = {},
  fileName,
}) => {
  const container = document.querySelector(containerSelector);

  container.style.background = backgroundColor;

  const { showHiddenDownloadElements } = hideElementsForDownload(container);

  const width = calcWidth(container);

  const blob = await toBlob(container, { style, width });
  const title = `${fileName}.png`;

  // Clean up styles
  container.style.removeProperty('background');

  showHiddenDownloadElements();

  return { blob, title };
};

export const downloadPNG = async ({
  backgroundColor,
  containerSelector,
  calcWidth = (el) => el.clientWidth,
  style = {},
  fileName,
}) => {
  const container = document.querySelector(containerSelector);

  // Temporarily set background to avoid screenshot with transparent background
  container.style.background = backgroundColor;

  const { showHiddenDownloadElements } = hideElementsForDownload(container);

  // Set width and style on container to allow left and right padding
  const width = calcWidth(container);

  const dataUrl = await toPng(container, { style, width });
  const title = `${fileName}.png`;

  // html-to-image's recommended workaround for Safari's issue with "painting" the image.
  // Loads image in browser and then downloads once loaded
  const imgEl = document.createElement('img');
  imgEl.src = dataUrl;

  const image = await new Promise((resolve) => {
    imgEl.onload = async () => {
      /* Safari needs some help getting a broken one out of the way by attempting
      a low quality version first that we just discard */
      await toPng(container, { quality: 0.01, style, width });

      const finalDataUrl = await toPng(container, { quality: 1, style, width });

      resolve(finalDataUrl);
    };
  });

  clickToDownload(image, title);

  container.style.removeProperty('background');

  showHiddenDownloadElements();

  return true;
};

export const downloadMultiplePNGs = async (pngConfigs) => {
  const downloadPromises = pngConfigs.map((config) => {
    const {
      backgroundColor, containerSelector, calcWidth, style, fileName,
    } = config;
    return downloadPNG({
      backgroundColor,
      containerSelector,
      calcWidth,
      style,
      fileName,
    });
  });

  return Promise.all(downloadPromises);
};

export const assignInsightsTableToCSVDownloadLink = ({
  tableRows, tableTotals, tableHeaders, projectName, dateRange, page, showTotalAsCategory = false,
}) => {
  const headers = tableHeaders?.length
    ? tableHeaders
    : ['Applied', 'Screening', 'Assessment', 'Offer', 'Hired'];

  const csvData = [
    ['', ...headers],
  ];

  if (tableTotals?.length) {
    const totalRow = ['Total', ...tableTotals.map((total) => getDashboardValue(total))];

    if (showTotalAsCategory) totalRow.splice(1, 0, 'Total');
    csvData.push(totalRow);
  }

  csvData.push(
    ...tableRows.map((row) => [
      row.label,
      ...Object.keys(row).filter((key) => !TABLE_KEYS_TO_EXCLUDE.includes(key))
        .map((key) => getDashboardValue(row[key])),
    ]),
  );

  const dateString = dateRange?.from && dateRange?.to
    ? `-${toFileNameFormat(dateRange.from)}-${toFileNameFormat(dateRange.to)}`
    : '';

  const name = `${projectName}-${page}${dateString}.csv`;

  arrayToCSVDownload({
    array: csvData, title: name, autoClick: true,
  });
};
